/**
 * ♿ High Contrast Accessibility Theme
 * Tema de alto contraste optimizado para accesibilidad WCAG 2.1 AAA
 * 
 * @description Diseño con máximo contraste para usuarios con discapacidades
 * visuales, cumple con todos los estándares de accesibilidad
 */

export const highContrastTheme = {
  name: 'High Contrast Accessibility',
  mode: 'light',
  accessibility: true,
  
  // 🎨 Paleta de alto contraste WCAG AAA
  palette: {
    primary: {
      main: '#000000',        // Negro puro para máximo contraste
      light: '#424242',       // Gris oscuro
      dark: '#000000',        // Negro puro
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#1565C0',        // Azul oscuro con excelente contraste
      light: '#1976D2',       // Azul medio
      dark: '#0D47A1',        // Azul muy oscuro
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#B71C1C',        // Rojo oscuro intenso
      light: '#D32F2F',       // Rojo medio
      dark: '#8B0000'         // Rojo muy oscuro
    },
    warning: {
      main: '#E65100',        // Naranja oscuro
      light: '#FF6F00',       // Naranja medio
      dark: '#BF360C'         // Naranja muy oscuro
    },
    info: {
      main: '#01579B',        // Azul información oscuro
      light: '#0277BD',       // Azul información medio
      dark: '#003C71'         // Azul información muy oscuro
    },
    success: {
      main: '#1B5E20',        // Verde oscuro
      light: '#2E7D32',       // Verde medio
      dark: '#0D4F12'         // Verde muy oscuro
    },
    
    // 🖼️ Fondos de máximo contraste
    background: {
      default: '#FFFFFF',      // Blanco puro
      paper: '#FFFFFF',        // Blanco puro para tarjetas
      elevated: '#F5F5F5'      // Gris muy claro para elevación
    },
    
    // 📝 Textos de máximo contraste
    text: {
      primary: '#000000',      // Negro puro para texto principal
      secondary: '#212121',    // Gris muy oscuro para secundario
      disabled: '#757575',     // Gris medio para deshabilitados
      hint: '#424242'         // Gris oscuro para hints
    },
    
    // 🎯 Colores de acción de alto contraste
    action: {
      active: '#000000',
      hover: 'rgba(0, 0, 0, 0.08)',
      selected: 'rgba(0, 0, 0, 0.12)',
      disabled: 'rgba(0, 0, 0, 0.38)',
      disabledBackground: 'rgba(0, 0, 0, 0.12)'
    },
    
    // 📏 Divisores de alto contraste
    divider: '#000000',

    // 🦅 Colores de marca AIFlock (alto contraste)
    brand: {
      main: '#7C3AED',          // AIFlock purple oscurecido para contraste
      light: '#9333EA',         // Purple estándar
      dark: '#5B21B6',          // Purple muy oscuro
      contrastText: '#FFFFFF'
    }
  },

  // ✨ Efectos visuales (shadows, overlays, hover states) - High Contrast
  effects: {
    // Sombras pronunciadas para máxima visibilidad
    shadows: {
      subtle: '0px 2px 4px rgba(0, 0, 0, 0.3)',
      medium: '0px 3px 8px rgba(0, 0, 0, 0.4)',
      strong: '0px 6px 24px rgba(0, 0, 0, 0.5)',
      brand: '0 4px 20px rgba(0, 0, 0, 0.35)',          // Sin color, solo contraste
      brandMedium: '0 3px 12px rgba(0, 0, 0, 0.3)',
      brandStrong: '0 6px 20px rgba(0, 0, 0, 0.4)'
    },

    // Estados hover muy visibles
    hover: {
      background: 'rgba(0, 0, 0, 0.08)',                // Hover muy visible
      primary: 'rgba(0, 0, 0, 0.12)',                   // Hover sin color
      card: '0px 3px 8px rgba(0, 0, 0, 0.3)',           // Sombra pronunciada
      cardBackground: 'rgba(0, 0, 0, 0.06)'             // Background visible
    },

    // Overlays de alto contraste
    overlay: {
      loading: 'rgba(0, 0, 0, 0.85)',                   // Overlay muy oscuro
      modal: 'rgba(0, 0, 0, 0.75)',                     // Overlay pronunciado
      subtle: 'rgba(0, 0, 0, 0.5)'                      // Overlay visible
    },

    // Estados de advertencia (alto contraste, sin color)
    warning: {
      background: 'rgba(0, 0, 0, 0.10)',                // Background sin color
      hover: 'rgba(0, 0, 0, 0.15)',                     // Hover sin color
      border: 'rgba(0, 0, 0, 0.3)'                      // Border visible
    }
  },

  // 🎛️ Configuración específica por sección
  sections: {
    // 💬 Chat Interface
    chat: {
      background: '#FFFFFF',
      messageBg: '#FFFFFF',
      inputBg: '#F5F5F5',
      userMessageBg: '#E3F2FD',
      botMessageBg: '#FFFFFF',
      border: '#000000',
      shadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
    },

    // 🎨 Flow Editor - MÁXIMO CONTRASTE
    editor: {
      canvasBg: '#FFFFFF',          // Blanco puro para máximo contraste
      nodeBg: '#FFFFFF',            // Nodos blancos puros
      nodeBorder: '#000000',        // Bordes negros gruesos
      nodeSelected: '#1565C0',      // Azul oscuro para selección
      nodeHover: '#F5F5F5',         // Gris muy claro para hover
      toolbarBg: '#FFFFFF',         // Toolbar blanco puro
      toolbarShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
      connectionDefault: '#000000',  // Conexiones negras
      connectionActive: '#1565C0',   // Conexiones activas azul oscuro
      miniMapBg: '#F5F5F5',
      gridColor: '#E0E0E0'          // Grid visible pero no molesto
    },

    // 👥 Roles Editor
    roles: {
      cardBg: '#FFFFFF',
      sidebarBg: '#FFFFFF',
      accent: '#000000',
      border: '#000000',
      hover: '#F5F5F5',
      selected: '#E3F2FD'
    },

    // ⚙️ Settings
    settings: {
      panelBg: '#FFFFFF',
      cardBg: '#FFFFFF',
      border: '#000000',
      accent: '#000000'
    },

    // 🎯 Onboarding
    onboarding: {
      bg: '#FFFFFF',
      cardBg: '#FFFFFF',
      accent: '#000000',
      progress: '#1565C0'
    }
  },

  // 🎭 Tipografía optimizada para accesibilidad
  typography: {
    fontFamily: '"Arial", "Helvetica Neue", Helvetica, sans-serif', // Fuente más legible
    fontSize: 16, // Tamaño base más grande
    fontWeightLight: 400, // Sin pesos muy ligeros
    fontWeightRegular: 400,
    fontWeightMedium: 700, // Salto directo a bold
    fontWeightBold: 700,
    
    h1: { fontSize: '3rem', fontWeight: 700, color: '#000000', lineHeight: 1.2 },
    h2: { fontSize: '2.5rem', fontWeight: 700, color: '#000000', lineHeight: 1.2 },
    h3: { fontSize: '2rem', fontWeight: 700, color: '#000000', lineHeight: 1.3 },
    h4: { fontSize: '1.75rem', fontWeight: 700, color: '#000000', lineHeight: 1.3 },
    h5: { fontSize: '1.5rem', fontWeight: 700, color: '#000000', lineHeight: 1.4 },
    h6: { fontSize: '1.25rem', fontWeight: 700, color: '#000000', lineHeight: 1.4 },
    
    body1: { fontSize: '1.125rem', color: '#000000', lineHeight: 1.8, fontWeight: 400 },
    body2: { fontSize: '1rem', color: '#212121', lineHeight: 1.8, fontWeight: 400 },
    caption: { fontSize: '0.875rem', color: '#212121', lineHeight: 1.6, fontWeight: 400 },
    button: { fontSize: '1rem', fontWeight: 700, textTransform: 'none' }
  },

  // ✨ Sombras de alto contraste
  shadows: {
    sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
    md: '0 4px 8px rgba(0, 0, 0, 0.3)',
    lg: '0 8px 16px rgba(0, 0, 0, 0.3)',
    xl: '0 16px 32px rgba(0, 0, 0, 0.3)',
    
    // Sombras específicas para componentes
    card: '0 4px 8px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)',
    modal: '0 32px 64px rgba(0, 0, 0, 0.4)',
    tooltip: '0 4px 8px rgba(0, 0, 0, 0.3)'
  },

  // 📏 Espaciado amplio para accesibilidad
  spacing: {
    xs: '0.5rem',     // 8px - más amplio
    sm: '0.75rem',    // 12px
    md: '1.25rem',    // 20px
    lg: '2rem',       // 32px
    xl: '2.5rem',     // 40px
    xxl: '4rem'       // 64px
  },

  // 🎭 Sin animaciones para accesibilidad
  animations: {
    fast: '0ms',      // Sin animaciones para usuarios sensibles
    normal: '0ms',
    slow: '0ms',
    easing: 'linear',
    
    // Animaciones específicas deshabilitadas
    fadeIn: 'none',
    slideUp: 'none',
    bounce: 'none'
  },

  // 📱 Breakpoints responsivos
  breakpoints: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920
  },

  // 🎨 Overrides de componentes MUI para accesibilidad
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#FFFFFF',
          color: '#000000',
          fontFamily: '"Arial", "Helvetica Neue", Helvetica, sans-serif',
          fontSize: '16px'
        }
      }
    },
    
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          borderRadius: 4, // Bordes menos redondeados
          border: '2px solid #000000' // Bordes gruesos y visibles
        }
      }
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '1rem',
          minHeight: '48px', // Botones más grandes para accesibilidad
          padding: '12px 24px',
          border: '2px solid transparent',
          '&:focus': {
            outline: '3px solid #1565C0', // Focus muy visible
            outlineOffset: '2px'
          }
        },
        contained: {
          background: '#000000',
          color: '#FFFFFF',
          border: '2px solid #000000',
          '&:hover': {
            background: '#212121',
            border: '2px solid #212121'
          }
        },
        outlined: {
          border: '2px solid #000000',
          color: '#000000',
          '&:hover': {
            background: '#F5F5F5',
            border: '2px solid #000000'
          }
        }
      }
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          border: '2px solid #000000',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
        }
      }
    },

    // Inputs más accesibles
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              border: '2px solid #000000'
            },
            '&:hover fieldset': {
              border: '2px solid #000000'
            },
            '&.Mui-focused fieldset': {
              border: '3px solid #1565C0'
            }
          }
        }
      }
    }
  }
};
